// src/components/settings/PaymentTab.jsx
import React, { memo, useCallback } from 'react';
import { CreditCard, Plus, CheckCircle, AlertTriangle } from 'lucide-react';

const PaymentTab = memo(({
  payoutMethods,
  loadingPayoutMethods,
  onNavigateToPayoutMethods,
  onSetDefaultMethod,
  onRemoveMethod
}) => {
  const parsePayoutDetails = useCallback((details) => {
    try {
      return JSON.parse(details);
    } catch (err) {
      return {};
    }
  }, []);

  const getBankIcon = useCallback((bankName) => {
    const bankIcons = {
      'OPay': '🟢',
      'GTBank': '🟣',
      'First Bank': '🔵',
      'Access Bank': '🔴',
      'Zenith Bank': '🟠',
      'UBA': '🟡',
    };

    for (const [key, icon] of Object.entries(bankIcons)) {
      if (bankName.toLowerCase().includes(key.toLowerCase())) {
        return icon;
      }
    }
    return '🏦';
  }, []);

  const handleSetDefault = useCallback((methodId) => {
    onSetDefaultMethod(methodId);
  }, [onSetDefaultMethod]);

  const handleRemove = useCallback((methodId) => {
    if (window.confirm('Are you sure you want to remove this payout method?')) {
      onRemoveMethod(methodId);
    }
  }, [onRemoveMethod]);

  if (loadingPayoutMethods) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 text-center">
        <div className="w-8 h-8 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">Loading payment methods...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Payment Method Button */}
      <button 
        onClick={onNavigateToPayoutMethods}
        className="w-full bg-[#ff660e] text-white py-3.5 rounded-lg font-semibold text-sm flex items-center justify-center"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Payment Method
      </button>

      {payoutMethods.length > 0 ? (
        <div className="space-y-3">
          <h3 className="font-bold text-black text-sm">Your Payment Methods</h3>
          {payoutMethods.map((method) => {
            const details = parsePayoutDetails(method.details);
            const verification = method.verification_status || {};
            
            return (
              <PaymentMethodCard
                key={method.payout_method_id}
                method={method}
                details={details}
                verification={verification}
                getBankIcon={getBankIcon}
                onSetDefault={handleSetDefault}
                onRemove={handleRemove}
              />
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <CreditCard className="w-6 h-6 text-gray-400" />
          </div>
          <h4 className="text-sm font-medium mb-2">No payment methods</h4>
          <p className="text-xs text-gray-600 mb-4">
            Add a payment method to receive payments
          </p>
        </div>
      )}
    </div>
  );
});

const PaymentMethodCard = memo(({ 
  method, 
  details, 
  verification, 
  getBankIcon, 
  onSetDefault, 
  onRemove 
}) => (
  <div className="bg-white rounded-xl shadow-sm p-4">
    <div className="flex items-start gap-3 mb-3">
      <div className="w-10 h-10 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
        <span className="text-xl">{getBankIcon(details.bank_name || 'Bank')}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-medium text-sm">{details.bank_name || 'Bank Account'}</h4>
          {method.is_default === 1 && (
            <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
              Default
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600">
          {details.account_number || 'N/A'} • {details.account_name || 'N/A'}
        </p>
      </div>
    </div>

    {/* Verification Status */}
    {verification.matches_profile !== undefined && (
      <div className={`p-2 rounded text-xs mb-3 ${
        verification.matches_profile 
          ? 'bg-green-50 text-green-700' 
          : 'bg-yellow-50 text-yellow-700'
      }`}>
        <div className="flex items-center">
          {verification.matches_profile ? (
            <CheckCircle className="w-3 h-3 mr-1" />
          ) : (
            <AlertTriangle className="w-3 h-3 mr-1" />
          )}
          <span>
            {verification.matches_profile 
              ? '✓ Matches your profile'
              : `⚠️ Doesn't match profile`}
          </span>
        </div>
      </div>
    )}

    {/* Actions */}
    <div className="flex gap-2 pt-3 border-t">
      {method.is_default !== 1 && (
        <button
          onClick={() => onSetDefault(method.payout_method_id)}
          className="flex-1 text-xs text-[#ff660e] font-medium py-2 border border-[#ff660e] rounded-lg hover:bg-orange-50"
        >
          Set Default
        </button>
      )}
      <button
        onClick={() => onRemove(method.payout_method_id)}
        className="flex-1 text-xs text-red-600 font-medium py-2 border border-red-200 rounded-lg hover:bg-red-50"
      >
        Remove
      </button>
    </div>
  </div>
));

export default PaymentTab;